/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { AnyAction, Dispatch } from "redux"
import {
  TroyWebTracker,
  getResponseErrorEventReason,
  getResponseErrorEventReasonForRTK,
} from "utils/tracking"
import { actionCreator } from "store/store"
import { actions as menuActions } from "store/duck/menu"
import { getActionSuccess, getActionFail } from "store/duck/utils/actionUtils"
import { AppState } from "store/store.type"
import { AxiosAction } from "order-manager/types/api/common.type"
import { FetchStoreListResponse } from "order-manager/types/api/orderFetchStoreList.type"
import { sleep } from "utils/misc"
import { storageHelper } from "utils/storage"
import { getGrabXValueFromLocalStorage, MerchantPortalFeatureFlag } from "grabx"
import { EventCategoryEng, EventActionEng } from "constants/tracking"
import {
  FetchStoreListRtkResponse,
  useLazyFetchStoreListQuery,
} from "queries/menuApi"
import { FetchStoreListResponse as PortalFetchStoreListResponse } from "types/api/menuFetchStoreList.type"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"

// singleton mutex to prevent duplicate dispatches
// issue: effects may be ran with old values from the store, esp. if they belong in the same render cycle
let isLoading = false

export interface Labels {
  LOAD_TIME: number
  LOADING_STATUS: string
  REASON: string
  ERROR_MSG: string
}

// Result summary shared by the portal and order-manager store-list fetch loops
export const buildFetchStoreListResultLabels = (
  t0: number,
  errors: string[],
): Labels => {
  let labels: Labels = {
    LOAD_TIME: performance.now() - t0,
    LOADING_STATUS: "SUCCESS",
    REASON: "",
    ERROR_MSG: "",
  }

  if (errors?.length > 0) {
    labels = {
      ...labels,
      LOADING_STATUS: "FAILURE",
      REASON: errors.join(","),
    }
  }

  return labels
}

type FetchStoreListTrigger = ReturnType<typeof useLazyFetchStoreListQuery>[0]

export const fetchAllStoreListFactory = (
  dispatch: Dispatch,
  isFetchingOutlets: boolean,
  rtkOptions?: {
    isRtkEnabled: boolean
    isRtkFlagLoading: boolean
    fetchStoreListTrigger: FetchStoreListTrigger
  },
) => async ({
  disableGlobalLoading,
  stopAfterFirstPage,
  onFetched,
  isWithItemPhotoCount = true,
}: {
  disableGlobalLoading?: boolean
  stopAfterFirstPage?: boolean
  onFetched?: (labels: Labels) => void
  isWithItemPhotoCount?: boolean
} = {}) => {
  if (isFetchingOutlets || isLoading) {
    return
  }

  // While migration flags are loading, do not fall back to old Redux: the enabled bit is
  // unknown until GrabX resolves. Legacy-then-RTK for the same user intent can double-call
  // APIs/middleware and splits RTK migration tracking. Wait for !isRtkFlagLoading; disable
  // the trigger UI while loading so clicks are not silently dropped.
  if (rtkOptions?.isRtkFlagLoading) {
    return
  }

  isLoading = true
  const countryCode = storageHelper.getParentEntityCountry() ?? ""

  const portalRetryTimeout = getGrabXValueFromLocalStorage<number>(
    MerchantPortalFeatureFlag.portalRetryTimeout,
  )

  const t0 = performance.now()
  if (!disableGlobalLoading) {
    dispatch(actionCreator.global.showLoading())
  }
  let offset = 0
  let hasMore = false
  const limit = 100
  const errors = []

  const isRtkEnabled = rtkOptions?.isRtkEnabled
  const sendFetchStoreListMigrationEvent = (fromRtkQuery: boolean) => {
    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.fetchStoreList,
      labels: {
        MERCHANT_ID: "",
        IS_RTK_ENABLED: !!isRtkEnabled,
        FROM_RTK_QUERY: fromRtkQuery,
        FROM_OLD_REDUX: !fromRtkQuery,
        ACTION_NAME: "fetchStoreList",
      },
    })
  }

  do {
    if (isRtkEnabled) {
      const previousAction = actionCreator.menu.fetchStoreList({
        countryCode,
        offset,
        limit,
        append: offset > 0,
        isWithItemPhotoCount,
        stopAfterFirstPage,
      })

      // RTK Query path
      // Dispatch pending action so isFetchingOutlets and outlets state update for other consumers
      if (offset === 0) {
        dispatch({
          type: menuActions.FETCH_STORE_LIST,
          payload: { append: false, stopAfterFirstPage },
        })
      }

      // eslint-disable-next-line no-await-in-loop
      const result = await rtkOptions!.fetchStoreListTrigger({
        countryCode,
        offset,
        limit,
        isWithItemPhotoCount,
      })

      if (result.error) {
        const rtkError = result.error as {
          status?: number | string
          data?: unknown
          headers?: Record<string, string>
        }
        const errorStatus = rtkError.status
        if (errorStatus === 429 && portalRetryTimeout && hasMore) {
          // eslint-disable-next-line no-await-in-loop
          await sleep(portalRetryTimeout)
          continue
        }

        errors.push(getResponseErrorEventReasonForRTK(result))

        // Dispatch fail action to update Redux state for other consumers
        dispatch({
          type: getActionFail(menuActions.FETCH_STORE_LIST),
          payload: { data: {} },
          error: {
            response: {
              data: rtkError.data,
              status:
                typeof rtkError.status === "number"
                  ? rtkError.status
                  : undefined,
              headers: rtkError.headers ?? {},
              config: {
                method: previousAction.payload.request.method,
                url: previousAction.payload.request.url,
              },
            },
          },
          meta: {
            previousAction,
          },
        })

        sendFetchStoreListMigrationEvent(true)

        break
      }

      const response = result.data as
        | FetchStoreListRtkResponse
        | PortalFetchStoreListResponse
        | undefined
      const isRtkResponse =
        response && "data" in response && "headers" in response
      const data = isRtkResponse ? response.data : response
      const headers = isRtkResponse ? response.headers : undefined
      hasMore = !!data?.metadata?.hasMore

      // Dispatch success action to update Redux state for other consumers
      dispatch({
        type: getActionSuccess(menuActions.FETCH_STORE_LIST),
        payload: { data, ...(headers ? { headers } : {}) },
        meta: {
          previousAction,
        },
      })

      sendFetchStoreListMigrationEvent(true)
    } else {
      // Old Redux path
      // eslint-disable-next-line no-await-in-loop
      const resp = (await dispatch(
        actionCreator.menu.fetchStoreList({
          countryCode,
          offset,
          limit,
          append: hasMore,
          isWithItemPhotoCount,
          stopAfterFirstPage,
        }) as AnyAction,
      )) as AxiosAction<FetchStoreListResponse>

      sendFetchStoreListMigrationEvent(false)

      if (resp?.type.endsWith("_FAIL")) {
        const errorStatus = (resp as any)?.error.response?.status
        /**
         * retry if got 429 (Too Many Requests)
         */
        if (errorStatus === 429 && portalRetryTimeout) {
          // eslint-disable-next-line no-await-in-loop
          await sleep(portalRetryTimeout)
          continue
        }

        const errorEventReason = getResponseErrorEventReason(resp)
        errors.push(errorEventReason)
      }

      hasMore = resp?.payload?.data?.metadata?.hasMore ?? false
    }

    offset += limit
    if (!hasMore || stopAfterFirstPage) {
      break
    }
    // eslint-disable-next-line no-await-in-loop
    await sleep(1000)
  } while (hasMore)

  const labels = buildFetchStoreListResultLabels(t0, errors)

  if (onFetched) {
    onFetched(labels)
  }

  if (!disableGlobalLoading) {
    dispatch(actionCreator.global.hideLoading())
  }

  isLoading = false
}

function useFetchAllStoreList() {
  const dispatch = useDispatch()
  const {
    menu: { isFetchingOutlets },
  } = useSelector((s: AppState) => s)

  const {
    flags: { fetchStoreList: isRtkFetchStoreListEnabled },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()

  const [fetchStoreListTrigger] = useLazyFetchStoreListQuery()

  const fetchAllStoreList = useMemo(
    () =>
      fetchAllStoreListFactory(dispatch, isFetchingOutlets, {
        isRtkEnabled: isRtkFetchStoreListEnabled,
        isRtkFlagLoading,
        fetchStoreListTrigger,
      }),
    [
      dispatch,
      isFetchingOutlets,
      isRtkFetchStoreListEnabled,
      isRtkFlagLoading,
      fetchStoreListTrigger,
    ],
  )
  return fetchAllStoreList
}

export default useFetchAllStoreList
